/**
 * Pure view helpers for the Pilots page, kept out of the components so the
 * summaries and labels can be unit tested without rendering React.
 */

export const PILOT_LENGTH_WEEKS = 3;
const PILOT_LENGTH_DAYS = PILOT_LENGTH_WEEKS * 7;
const UNASSIGNED = "Unassigned";

/** Past the three-week playbook and not yet in the internal review. */
export function isPilotOverdue(pilot) {
  if (pilot.daysInPilot === null || pilot.daysInPilot === undefined) {
    return false;
  }

  return pilot.daysInPilot > PILOT_LENGTH_DAYS && pilot.subStage?.phase !== "review";
}

function countBy(pilots, key) {
  const counts = new Map();
  pilots.forEach((pilot) => {
    const name = pilot[key] || UNASSIGNED;
    counts.set(name, (counts.get(name) || 0) + 1);
  });

  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

/**
 * Headline numbers for the page.
 *
 * @param {object[]} active running pilots
 * @param {object[]} past closed pilots
 */
export function summarizePilots(active = [], past = []) {
  const withDays = active.filter(
    (pilot) => pilot.daysInPilot !== null && pilot.daysInPilot !== undefined,
  );
  const won = past.filter((pilot) => pilot.isWon).length;
  const fdeLoad = countBy(active, "fde");

  const linked = active.filter((pilot) => pilot.recentUsage);

  return {
    runningCount: active.length,
    linkedCount: linked.length,
    usingThisWeekCount: linked.filter((pilot) => pilot.recentUsage.activeUsers > 0).length,
    fdeLoad,
    fdeCount: fdeLoad.filter((entry) => entry.name !== UNASSIGNED).length,
    unassignedCount: active.filter((pilot) => !pilot.fde).length,
    ownerLoad: countBy(active, "owner"),
    avgDaysInPilot: withDays.length
      ? Math.round(
          withDays.reduce((sum, pilot) => sum + pilot.daysInPilot, 0) /
            withDays.length,
        )
      : null,
    overdueCount: active.filter(isPilotOverdue).length,
    pastWon: won,
    pastLost: past.length - won,
    winRate: past.length ? won / past.length : null,
  };
}

/**
 * Where a pilot is in the three-week playbook, as a label and a 0–1 progress
 * value (null when HubSpot has no playbook step to go on).
 */
export function playbookProgress(pilot) {
  const subStage = pilot.subStage;
  if (!subStage) {
    return { label: "No playbook step set", value: null };
  }
  if (subStage.phase === "setup") {
    return { label: "Setting up", value: 0 };
  }
  if (subStage.phase === "running") {
    return {
      label: `Week ${subStage.week} of ${PILOT_LENGTH_WEEKS} done`,
      value: subStage.week / PILOT_LENGTH_WEEKS,
    };
  }
  if (subStage.phase === "review") {
    return { label: "Internal review done", value: 1 };
  }

  return { label: subStage.label, value: null };
}

export function formatMoney(amount, { cents = false } = {}) {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) {
    return "—";
  }
  const digits = cents ? 2 : 0;

  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function formatNumber(value, digits = 0) {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "—";
  }

  return value.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

export function formatPercent(ratio, digits = 0) {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) {
    return "—";
  }

  return `${(ratio * 100).toFixed(digits)}%`;
}

export function formatDate(ms) {
  if (!ms) {
    return "—";
  }

  return new Date(ms).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Plain-text ROI summary for pasting into an email or a deck.
 *
 * @param {string} clientLabel
 * @param {{ loansPerMonth: number, hourlyCost: number }} inputs
 * @param {ReturnType<import("./calculators").calculateRoi>} result
 */
export function buildRoiSummary(clientLabel, inputs, result) {
  const lines = [
    `Addy ROI estimate: ${clientLabel}`,
    `- ${formatNumber(Number(inputs.loansPerMonth) || 0)} loans a month; hands-on review drops from ${formatNumber(
      result.minutesTodayPerLoan,
      1,
    )} to ${formatNumber(result.minutesWithAddyPerLoan, 1)} min per loan (${formatPercent(
      result.timeReduction,
    )} less).`,
    `- ${formatNumber(result.monthlyHoursSaved)} staff hours back a month (${formatNumber(
      result.fteFreed,
      1,
    )} full-time people), worth ${formatMoney(result.monthlyLaborSavings)} a month at ${formatMoney(
      Number(inputs.hourlyCost) || 0,
    )}/h.`,
    `- Addy: ${formatMoney(result.monthlyAddyCost)} a month. Net: ${formatMoney(
      result.annualNetSavings,
    )} a year${result.roiMultiple === null ? "" : ` (${formatNumber(result.roiMultiple, 2)}x return)`}.`,
  ];
  if (result.turnTimeDaysSaved) {
    lines.push(`- Turn time: about ${formatNumber(result.turnTimeDaysSaved, 1)} days faster per loan.`);
  }
  lines.push("Calculated from the numbers above and Addy's pricing; change any of them to update it.");

  return lines.join("\n");
}

export function hubspotCompanyUrl(companyId) {
  return `https://app.hubspot.com/contacts/objects/0-2/${companyId}/view`;
}

export function hubspotDealUrl(dealId) {
  return `https://app.hubspot.com/contacts/objects/0-3/${dealId}/view`;
}
